import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import { Box, Grid, Skeleton, Typography, useTheme } from '@mui/material';
import React, { FC } from 'react';
import strings from 'common/Translation/Translate';
import { formatCurrencyWithSymbol, getCurrencySymbol } from 'utils/hooks/getCurrency';

export interface DashboardAlertsMetrics {
  eventsTodayWithoutBeo: number;
  paymentDueCount: number;
  paymentDueAmount: number;
  tentativeCount: number;
  tentativePotentialTotal: number;
}

export interface DashboardTodayActionsMetrics {
  eventsScheduled: number;
  beoPending: number;
  paymentsToCollect: number;
  tentativeToConvert: number;
}

interface DashboardAlertsAndActionsProps {
  isLoading: boolean;
  alerts: DashboardAlertsMetrics;
  today: DashboardTodayActionsMetrics;
}

const formatINR = (value: number) =>
  formatCurrencyWithSymbol(value);

/** Compact lakhs display for large round numbers. */
const formatINRCompact = (value: number) => {
  const abs = Math.abs(value);
  if (abs >= 100000) {
    const lakhs = value / 100000;
    const rounded = Math.round(lakhs * 10) / 10;
    return `${getCurrencySymbol()}${Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)}L`;
  }
  return formatINR(value);
};

const Bullet: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="li"
    sx={{
      fontSize: 13.5,
      lineHeight: 1.65,
      color: 'text.primary',
      fontWeight: 500,
      pl: 0.5,
    }}>
    {children}
  </Typography>
);

const DashboardAlertsAndActions: FC<DashboardAlertsAndActionsProps> = ({ isLoading, alerts, today }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Skeleton variant="text" width={220} height={28} sx={{ mb: 1 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const attentionBullets: React.ReactNode[] = [];
  if (alerts.eventsTodayWithoutBeo > 0) {
    attentionBullets.push(
      <Bullet key="beo">
        {alerts.eventsTodayWithoutBeo} event{alerts.eventsTodayWithoutBeo === 1 ? '' : 's'} today without BEO
      </Bullet>,
    );
  }
  if (alerts.paymentDueCount > 0) {
    attentionBullets.push(
      <Bullet key="pay">
        {alerts.paymentDueCount} booking{alerts.paymentDueCount === 1 ? '' : 's'} with pending payments (
        {formatINR(alerts.paymentDueAmount)})
      </Bullet>,
    );
  }
  if (alerts.tentativeCount > 0) {
    attentionBullets.push(
      <Bullet key="tent">
        {alerts.tentativeCount} tentative booking{alerts.tentativeCount === 1 ? '' : 's'} worth{' '}
        {formatINRCompact(alerts.tentativePotentialTotal)} not converted yet
      </Bullet>,
    );
  }

  const todayBullets: React.ReactNode[] = [
    <Bullet key="ev">
      {today.eventsScheduled} Event{today.eventsScheduled === 1 ? '' : 's'} scheduled
    </Bullet>,
    <Bullet key="beo">
      {today.beoPending} BEO{today.beoPending === 1 ? '' : 's'} pending
    </Bullet>,
    <Bullet key="pay">
      {today.paymentsToCollect} payment{today.paymentsToCollect === 1 ? '' : 's'} to collect
    </Bullet>,
    <Bullet key="tent">
      {today.tentativeToConvert} tentative{today.tentativeToConvert === 1 ? '' : 's'} to convert
    </Bullet>,
  ];

  const hasAnyTodayActivity =
    today.eventsScheduled > 0 || today.beoPending > 0 || today.paymentsToCollect > 0 || today.tentativeToConvert > 0;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 1,
          mb: 1,
        }}>
        Alerts / Insights
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: '100%',
              borderRadius: 2,
              border: `1px solid ${theme.palette.warning.light}`,
              background: 'linear-gradient(135deg, rgba(254, 243, 199, 0.55) 0%, rgba(255, 255, 255, 0.95) 100%)',
              p: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <WarningAmberRoundedIcon sx={{ color: '#B45309', fontSize: 26 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#92400e' }}>Attention Needed</Typography>
            </Box>
            {attentionBullets.length > 0 ? (
              <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'disc' }}>
                {attentionBullets}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
                Nothing urgent right now.
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: '100%',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: 'linear-gradient(135deg, rgba(239, 246, 255, 0.65) 0%, rgba(255, 255, 255, 0.96) 100%)',
              p: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <TodayRoundedIcon sx={{ color: theme.palette.primary.main, fontSize: 26, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary' }}>
                {'Today\u2019s Actions'}
              </Typography>
            </Box>
            {hasAnyTodayActivity ? (
              <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'disc' }}>
                {todayBullets}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600, lineHeight: 1.55 }}>
                {strings.dashboard_today_actions_empty}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardAlertsAndActions;
